/**
 * Transforms configuration data into a structured format for GlobalConfigList.
 *
 * This utility function processes an array of configuration data and organizes
 * it into two main categories:
 * 1. Merged port rows: Groups configurations by port numbers (Monitor and TCP),
 *    merging them into a single object with additional metadata.
 * 2. Other rows: Includes configurations that do not match the Monitor or TCP patterns.
 *
 * @param {Array<Object>} data - The input configuration data. Each object in the array
 * contains the following properties:
 *   - {string} key - The key identifying the configuration (e.g., "Monitor-1", "TCP-2").
 *   - {boolean} [encrypted] - Indicates if the configuration is encrypted.
 *   - {boolean} [_active] - Indicates if the configuration is active.
 *   - {string} [value] - The value of the configuration.
 *   - {string} [comment] - A comment associated with the configuration.
 *
 * @returns {Array<Object>} - The transformed configuration data, consisting of:
 *   - Merged port rows: Objects with the following properties:
 *     - {string} id - The unique identifier for the port configuration.
 *     - {string} key - The key for the port configuration.
 *     - {string} value - The value of the port configuration (empty string by default).
 *     - {string} comment - A comment describing the merged port configuration.
 *     - {Object} portConfigError - An object indicating missing Monitor or TCP configurations.
 *     - {boolean} encrypted - Indicates if any of the child configurations are encrypted.
 *     - {string|undefined} note - A note about encryption, if applicable.
 *     - {boolean} _active - Indicates if any of the child configurations are active.
 *     - {string} type - The type of configuration ("port").
 *     - {number} portNumber - The port number associated with the configuration.
 *     - {Object} children - The grouped Monitor and TCP configurations for the port.
 *   - Other rows: Objects with the original properties plus:
 *     - {string} type - The type of configuration ("other").
 */
// Utility to transform config data for GlobalConfigList

export function transformGlobalConfigData(data) {
  const portGroups = {}
  const otherRows = []

  data.forEach((row) => {
    const monitorMatch = row.key.match(/^Monitor-(\d+)$/)
    const tcpMatch = row.key.match(/^TCP-(\d+)$/)

    if (monitorMatch) {
      const portNumber = parseInt(monitorMatch[1], 10)
      if (!portGroups[portNumber]) {
        portGroups[portNumber] = {}
      }
      portGroups[portNumber].monitor = row
    } else if (tcpMatch) {
      const portNumber = parseInt(tcpMatch[1], 10)
      if (!portGroups[portNumber]) {
        portGroups[portNumber] = {}
      }
      portGroups[portNumber].tcp = row
    } else {
      otherRows.push({ ...row, type: 'other' })
    }
  })

  const mergedPortRows = Object.entries(portGroups).map(
    ([portNumberStr, children]) => {
      const portNumber = parseInt(portNumberStr, 10)
      const isEncrypted = Object.values(children).some(
        (child) => child.encrypted
      )
      const encryptedConfigs = Object.values(children).filter(
        (child) => child.encrypted
      )
      const combinedActiveStatus = Object.values(children).some(
        (child) => child._active
      )
      return {
        id: `Port-${portNumber}`,
        key: `Port-${portNumber}`,
        value: '',
        comment: 'Merged port configuration',
        portConfigError: {
          monitor: children.monitor ? null : 'Missing Monitor port config',
          tcp: children.tcp ? null : 'Missing TCP port config',
        },
        encrypted: isEncrypted,
        note:
          encryptedConfigs.length === 1
            ? `${encryptedConfigs[0].key} is encrypted`
            : undefined,
        _active: combinedActiveStatus,
        type: 'port',
        portNumber: portNumber,
        children: children,
      }
    }
  )

  return [...mergedPortRows, ...otherRows]
}
